import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { japanPrefectures } from '../../../mocks/japanPrefectures';
import type { PrefectureEvent } from '../../../mocks/events';
import { TicketPurchaseModal } from '../../event/components/TicketPurchaseModal';

// ─── 型定義 ───────────────────────────────────────────────────────────────────

interface CalendarEvent extends PrefectureEvent {
  attributeName?: string;
  displayColor?: string;
}

interface DayCell {
  date: Date | null;
  dateStr: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

// ─── 定数 ─────────────────────────────────────────────────────────────────────

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const MONTHS = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];
const DEFAULT_COLOR = '#00d9ff';

// ─── ユーティリティ ────────────────────────────────────────────────────────────

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildCalendarGrid(year: number, month: number, events: CalendarEvent[]): DayCell[] {
  const today = toDateStr(new Date());
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay();

  const eventMap = new Map<string, CalendarEvent[]>();
  events.forEach(ev => {
    const key = ev.date.slice(0, 10);
    if (!eventMap.has(key)) eventMap.set(key, []);
    eventMap.get(key)!.push(ev);
  });

  const cells: DayCell[] = [];

  for (let i = 0; i < startDow; i++) {
    cells.push({ date: null, dateStr: '', isCurrentMonth: false, isToday: false, events: [] });
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    const dateStr = toDateStr(date);
    cells.push({
      date,
      dateStr,
      isCurrentMonth: true,
      isToday: dateStr === today,
      events: eventMap.get(dateStr) || [],
    });
  }

  const remaining = 42 - cells.length;
  for (let i = 0; i < remaining; i++) {
    cells.push({ date: null, dateStr: '', isCurrentMonth: false, isToday: false, events: [] });
  }

  return cells;
}

// ─── ReserveButton ────────────────────────────────────────────────────────────

function ReserveButton({
  ev,
  color,
  onReserve,
}: {
  ev: CalendarEvent;
  color: string;
  onReserve: (ev: CalendarEvent) => void;
}) {
  const isSoldOut = ev.sold_out;
  const isFreeEvent = !ev.is_paid_event || !ev.ticket_price || ev.ticket_price === 0;
  const capacity = ev.max_capacity ?? null;
  const reserved = ev.current_reservations ?? 0;
  const isFull = capacity !== null && reserved >= capacity;

  if (isSoldOut || isFull) {
    return (
      <div
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold tracking-wider opacity-50 cursor-not-allowed"
        style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          color: '#f87171',
        }}
      >
        <i className="ri-close-circle-line text-sm" />
        満員・受付終了
      </div>
    );
  }

  return (
    <button
      onClick={() => onReserve(ev)}
      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold tracking-wider transition-all duration-200 cursor-pointer hover:opacity-90 active:scale-95 whitespace-nowrap"
      style={{
        background: isFreeEvent
          ? 'linear-gradient(135deg, rgba(34,197,94,0.25), rgba(34,197,94,0.12))'
          : `linear-gradient(135deg, ${color}30, ${color}15)`,
        border: isFreeEvent ? '1px solid rgba(34,197,94,0.5)' : `1px solid ${color}60`,
        color: isFreeEvent ? '#4ade80' : color,
        boxShadow: isFreeEvent ? '0 0 14px rgba(34,197,94,0.2)' : `0 0 14px ${color}20`,
      }}
    >
      <i className="ri-ticket-2-line text-sm" />
      {isFreeEvent ? '無料で予約する' : '予約する'}
    </button>
  );
}

// ─── EventDayModal ────────────────────────────────────────────────────────────

function EventDayModal({
  dateStr,
  events,
  onClose,
  onReserve,
}: {
  dateStr: string;
  events: CalendarEvent[];
  onClose: () => void;
  onReserve: (ev: CalendarEvent) => void;
}) {
  const navigate = useNavigate();
  const date = new Date(dateStr);
  const weekday = WEEKDAYS[date.getDay()];
  const label = `${date.getMonth() + 1}月${date.getDate()}日（${weekday}）`;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        className="relative w-full max-w-xl rounded-2xl border border-teal-500/20 overflow-hidden"
        style={{ background: '#0d0d0d', maxHeight: '85vh' }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500" style={{ boxShadow: '0 0 8px #ef4444' }} />
            <span className="text-white font-bold tracking-wider text-sm">{label}</span>
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: 'rgba(0,217,255,0.15)', color: '#00d9ff', border: '1px solid rgba(0,217,255,0.3)' }}
            >
              {events.length} 件
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-400 text-sm" />
          </button>
        </div>

        {/* Event list */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 64px)' }}>
          {events.map((ev, idx) => {
            const color = ev.displayColor || ev.color || DEFAULT_COLOR;
            const isFree = ev.price?.toLowerCase().includes('無料') || ev.price?.toLowerCase().includes('free') || ev.price === '0';
            const isSoldOut = ev.sold_out;
            const capacity = ev.max_capacity ?? null;
            const reserved = ev.current_reservations ?? 0;
            const remaining = capacity !== null ? capacity - reserved : null;
            const remainingRatio = capacity ? reserved / capacity : 0;

            return (
              <div
                key={ev.id}
                className="border-b border-white/5 last:border-b-0"
                style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
              >
                {/* サムネイル */}
                <div className="relative w-full h-36 overflow-hidden">
                  <img
                    src={ev.thumbnail}
                    alt={ev.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/30 to-transparent" />

                  {/* バッジ群 */}
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {ev.isFeatured && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-green-400/20 border border-green-400/40 text-green-400">
                        FEATURED
                      </span>
                    )}
                    {ev.created_by && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider flex items-center gap-1"
                        style={{ background: 'rgba(0,217,255,0.15)', border: '1px solid rgba(0,217,255,0.4)', color: '#00d9ff' }}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#00d9ff]" style={{ boxShadow: '0 0 4px #00d9ff' }} />
                        NODE EVENT
                      </span>
                    )}
                    {isSoldOut && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-red-500/20 border border-red-500/40 text-red-400">
                        SOLD OUT
                      </span>
                    )}
                  </div>

                  {/* カテゴリバッジ */}
                  <div className="absolute top-3 right-3">
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm"
                      style={{ background: `${color}30`, color, border: `1px solid ${color}40` }}
                    >
                      {ev.attributeName || ev.category}
                    </span>
                  </div>

                  {/* 時間・会場（サムネイル下部） */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold backdrop-blur-md"
                        style={{ background: 'rgba(0,0,0,0.6)', color: '#e5e7eb' }}
                      >
                        <i className="ri-time-line text-teal-400" />
                        {ev.time}
                      </span>
                      <span
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold backdrop-blur-md max-w-[140px] truncate"
                        style={{ background: 'rgba(0,0,0,0.6)', color: '#e5e7eb' }}
                      >
                        <i className="ri-map-pin-line text-red-400" />
                        {ev.venue}
                      </span>
                    </div>
                    {/* 価格 */}
                    <span
                      className="px-2 py-1 rounded-lg text-[11px] font-bold backdrop-blur-md"
                      style={{
                        background: isFree ? 'rgba(34,197,94,0.2)' : `${color}20`,
                        color: isFree ? '#4ade80' : color,
                        border: `1px solid ${isFree ? 'rgba(34,197,94,0.4)' : `${color}40`}`,
                      }}
                    >
                      <i className="ri-ticket-line mr-1" />
                      {ev.price || '未定'}
                    </span>
                  </div>
                </div>

                {/* テキスト情報 */}
                <div className="px-4 pt-3 pb-4">
                  {/* タイトル */}
                  <h4 className="text-white text-sm font-bold tracking-wide mb-1.5 leading-snug">
                    {ev.title}
                  </h4>

                  {/* 説明文 */}
                  {ev.description && (
                    <p className="text-gray-500 text-[11px] leading-relaxed mb-3 line-clamp-2">
                      {ev.description}
                    </p>
                  )}

                  {/* アーティスト */}
                  {(ev.artistNames?.length > 0 || ev.artistIds?.length > 0) && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className="text-gray-600 text-[10px] flex items-center gap-1 mr-0.5">
                        <i className="ri-user-music-line" /> 出演：
                      </span>
                      {ev.artistNames?.map((name, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded-full text-[10px] tracking-wider border"
                          style={{ background: `${color}10`, color, borderColor: `${color}30` }}
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 定員・残席バー */}
                  {capacity !== null && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-600 text-[10px] flex items-center gap-1">
                          <i className="ri-group-line" /> 定員 {capacity}名
                        </span>
                        {isSoldOut ? (
                          <span className="text-red-400 text-[10px] font-bold">満員</span>
                        ) : remaining !== null ? (
                          <span
                            className="text-[10px] font-bold"
                            style={{ color: remaining <= 5 ? '#f87171' : remaining <= 20 ? '#fb923c' : '#4ade80' }}
                          >
                            残り {remaining} 席
                          </span>
                        ) : null}
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(remainingRatio * 100, 100)}%`,
                            background: remainingRatio >= 0.8
                              ? '#ef4444'
                              : remainingRatio >= 0.5
                              ? '#f97316'
                              : color,
                          }}
                        />
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="text-gray-700 text-[9px]">0</span>
                        <span className="text-gray-700 text-[9px]">{reserved} / {capacity}</span>
                      </div>
                    </div>
                  )}

                  {/* 住所 */}
                  {ev.address && (
                    <div className="flex items-start gap-1.5 mb-3">
                      <i className="ri-map-pin-2-line text-gray-600 text-xs mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-[10px] leading-relaxed">{ev.address}</span>
                    </div>
                  )}

                  {/* ボタン群 */}
                  <div className="flex flex-col gap-2">
                    {/* 予約ボタン */}
                    <ReserveButton ev={ev} color={color} onReserve={onReserve} />

                    {/* 詳細ボタン */}
                    <button
                      onClick={() => navigate(`/event/${ev.id}`)}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold tracking-wider transition-all duration-200 cursor-pointer hover:opacity-80 active:scale-95 whitespace-nowrap"
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#9ca3af',
                      }}
                    >
                      <i className="ri-arrow-right-circle-line text-sm" />
                      イベント詳細を見る
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── EventCalendarView (メイン) ───────────────────────────────────────────────

export default function EventCalendarView() {
  const { profile } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null);
  const [selectedPrefecture, setSelectedPrefecture] = useState<string>('');
  const [prefOpen, setPrefOpen] = useState(false);

  // 予約モーダル用
  const [reserveTarget, setReserveTarget] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    if (profile?.prefecture) {
      setSelectedPrefecture(profile.prefecture);
    }
  }, [profile]);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const firstOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastOfMonth = `${year}-${String(month + 1).padStart(2, '00')}-${new Date(year, month + 1, 0).getDate()}`;

        let query = supabase
          .from('events')
          .select('*')
          .gte('date', firstOfMonth)
          .lte('date', lastOfMonth)
          .order('date', { ascending: true });

        if (selectedPrefecture) {
          query = query.eq('prefecture', selectedPrefecture);
        }

        const { data, error } = await query;
        if (error) throw error;

        const attrIds = [...new Set((data || []).map((e: any) => e.attribute_id).filter(Boolean))];
        const attrMap = new Map<string, any>();
        if (attrIds.length > 0) {
          const { data: attrs } = await supabase
            .from('event_attributes')
            .select('id, name, color')
            .in('id', attrIds);
          (attrs || []).forEach((a: any) => attrMap.set(a.id, a));
        }

        const enriched: CalendarEvent[] = (data || []).map((item: any) => {
          const attr = item.attribute_id ? attrMap.get(item.attribute_id) : null;
          return {
            id: item.id,
            title: item.title || '',
            date: item.date || '',
            time: item.time || '',
            venue: item.venue || '',
            address: item.address || '',
            prefecture: item.prefecture || '',
            description: item.description || '',
            price: item.price || '',
            category: item.category || 'LIVE',
            thumbnail: item.thumbnail || item.image_url || '',
            gallery: item.gallery || [],
            artistIds: item.artist_ids || [],
            artistNames: item.artist_names || [],
            isFeatured: item.is_featured || false,
            color: item.color || DEFAULT_COLOR,
            attributeId: item.attribute_id || '',
            mainGenreId: item.main_genre_id || '',
            mainPartId: item.main_part_id || '',
            is_paid_event: item.is_paid_event || false,
            ticket_price: item.ticket_price ?? null,
            max_capacity: item.max_capacity ?? null,
            current_reservations: item.current_reservations || 0,
            sold_out: item.sold_out || false,
            created_by: item.created_by || null,
            attributeName: attr?.name,
            displayColor: attr?.color || item.color || DEFAULT_COLOR,
          };
        });

        setEvents(enriched);
      } catch (err) {
        console.error('Calendar events fetch error:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [year, month, selectedPrefecture]);

  const cells = useMemo(() => buildCalendarGrid(year, month, events), [year, month, events]);

  const selectedEvents = useMemo(
    () => (selectedDateStr ? events.filter(e => e.date.slice(0, 10) === selectedDateStr) : []),
    [selectedDateStr, events]
  );

  const prefLabel = useMemo(() => {
    if (!selectedPrefecture) return '全国';
    return japanPrefectures.find(p => p.id === selectedPrefecture)?.name || selectedPrefecture;
  }, [selectedPrefecture]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  // 予約完了後にイベントデータを再取得
  const handlePurchaseComplete = () => {
    setReserveTarget(null);
    // 予約数を反映するため再フェッチ
    const fetchUpdated = async () => {
      if (!reserveTarget) return;
      const { data } = await supabase
        .from('events')
        .select('current_reservations, sold_out')
        .eq('id', reserveTarget.id)
        .maybeSingle();
      if (data) {
        setEvents(prev =>
          prev.map(ev =>
            ev.id === reserveTarget.id
              ? { ...ev, current_reservations: data.current_reservations, sold_out: data.sold_out }
              : ev
          )
        );
      }
    };
    fetchUpdated();
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-6">

      {/* ── 都道府県トグル ── */}
      <div className="mb-5 relative">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1.5 h-1.5 rounded-full bg-teal-400" style={{ boxShadow: '0 0 8px #2dd4bf' }} />
          <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Prefecture Filter</span>
        </div>
        <button
          onClick={() => setPrefOpen(o => !o)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/8 transition-all cursor-pointer"
        >
          <i className="ri-map-pin-2-line text-teal-400 text-sm w-4 h-4 flex items-center justify-center" />
          <span className="text-white text-sm font-bold tracking-wider">{prefLabel}</span>
          <i className={`ri-arrow-${prefOpen ? 'up' : 'down'}-s-line text-gray-500 text-sm ml-1 w-4 h-4 flex items-center justify-center`} />
        </button>

        <AnimatePresence>
          {prefOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full left-0 mt-2 z-30 rounded-xl border border-white/10 overflow-hidden"
              style={{ background: '#111', width: 320, maxHeight: 320, overflowY: 'auto' }}
            >
              <button
                onClick={() => { setSelectedPrefecture(''); setPrefOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                  selectedPrefecture === '' ? 'text-teal-400 bg-teal-500/10' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                全国
              </button>
              {japanPrefectures.map(pref => (
                <button
                  key={pref.id}
                  onClick={() => { setSelectedPrefecture(pref.id); setPrefOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer flex items-center justify-between ${
                    selectedPrefecture === pref.id ? 'text-teal-400 bg-teal-500/10' : 'text-gray-300 hover:bg-white/5'
                  }`}
                >
                  <span>{pref.name}</span>
                  {selectedPrefecture === pref.id && (
                    <i className="ri-check-line text-teal-400 text-xs w-4 h-4 flex items-center justify-center" />
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── カレンダーヘッダー ── */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={prevMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-gray-300 text-lg w-5 h-5 flex items-center justify-center" />
        </button>

        <div className="text-center">
          <p className="text-white font-bold text-lg tracking-wider">
            {year}年 {MONTHS[month]}
          </p>
          {!loading && (
            <p className="text-teal-400 text-xs tracking-wider mt-0.5">
              {events.length} 件のイベント
            </p>
          )}
        </div>

        <button
          onClick={nextMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-gray-300 text-lg w-5 h-5 flex items-center justify-center" />
        </button>
      </div>

      {/* ── 曜日ヘッダー ── */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((wd, i) => (
          <div
            key={wd}
            className={`text-center text-[11px] font-bold tracking-wider py-2 ${
              i === 0 ? 'text-red-400' : i === 6 ? 'text-teal-400' : 'text-gray-500'
            }`}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* ── カレンダーグリッド ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative w-10 h-10 mb-3">
            <div className="absolute inset-0 border-2 border-teal-400/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-gray-600 text-xs tracking-wider">LOADING...</p>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            const dow = idx % 7;
            const hasEvents = cell.events.length > 0;
            const isSelected = cell.dateStr === selectedDateStr;

            return (
              <motion.div
                key={idx}
                whileHover={hasEvents ? { scale: 1.05 } : {}}
                whileTap={hasEvents ? { scale: 0.97 } : {}}
                onClick={() => {
                  if (hasEvents && cell.dateStr) {
                    setSelectedDateStr(cell.dateStr);
                  }
                }}
                className={`relative aspect-square rounded-xl flex flex-col items-center justify-start pt-1.5 transition-all duration-200 ${
                  !cell.isCurrentMonth ? 'opacity-0 pointer-events-none' :
                  hasEvents ? 'cursor-pointer' : 'cursor-default'
                } ${
                  isSelected
                    ? 'border border-teal-500/60 bg-teal-500/10'
                    : hasEvents
                    ? 'border border-white/8 bg-white/3 hover:border-red-500/40 hover:bg-red-500/5'
                    : 'border border-transparent'
                }`}
                style={
                  isSelected
                    ? { boxShadow: '0 0 16px rgba(45,212,191,0.2)' }
                    : {}
                }
              >
                <span
                  className={`text-xs font-bold leading-none ${
                    cell.isToday
                      ? 'text-white'
                      : dow === 0
                      ? 'text-red-400'
                      : dow === 6
                      ? 'text-teal-400'
                      : hasEvents
                      ? 'text-gray-200'
                      : 'text-gray-600'
                  }`}
                >
                  {cell.date?.getDate()}
                </span>

                {cell.isToday && (
                  <div
                    className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-teal-400"
                    style={{ boxShadow: '0 0 6px #2dd4bf' }}
                  />
                )}

                {hasEvents && (
                  <div className="mt-1 flex flex-col items-center gap-0.5">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        background: 'rgba(239,68,68,0.2)',
                        border: '1.5px solid rgba(239,68,68,0.7)',
                        boxShadow: '0 0 8px rgba(239,68,68,0.4)',
                      }}
                    >
                      <span className="text-red-400 text-[9px] font-bold">{cell.events.length}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── 凡例 ── */}
      <div className="flex items-center gap-4 mt-4 px-1">
        <div className="flex items-center gap-1.5">
          <div
            className="w-4 h-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.2)', border: '1.5px solid rgba(239,68,68,0.7)' }}
          >
            <span className="text-red-400 text-[8px] font-bold">1</span>
          </div>
          <span className="text-gray-600 text-[10px]">イベントあり（件数）</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-teal-400" style={{ boxShadow: '0 0 6px #2dd4bf' }} />
          <span className="text-gray-600 text-[10px]">今日</span>
        </div>
      </div>

      {/* ── 日付選択モーダル ── */}
      <AnimatePresence>
        {selectedDateStr && selectedEvents.length > 0 && (
          <EventDayModal
            dateStr={selectedDateStr}
            events={selectedEvents}
            onClose={() => setSelectedDateStr(null)}
            onReserve={(ev) => {
              setReserveTarget(ev);
            }}
          />
        )}
      </AnimatePresence>

      {/* ── 予約モーダル ── */}
      {reserveTarget && (
        <TicketPurchaseModal
          isOpen={!!reserveTarget}
          onClose={() => setReserveTarget(null)}
          onPurchaseComplete={handlePurchaseComplete}
          eventId={reserveTarget.id}
          eventTitle={reserveTarget.title}
          ticketPrice={reserveTarget.ticket_price ?? 0}
          maxCapacity={reserveTarget.max_capacity ?? 999}
          currentReservations={reserveTarget.current_reservations ?? 0}
          displayColor={reserveTarget.displayColor || reserveTarget.color || DEFAULT_COLOR}
        />
      )}
    </div>
  );
}
